import React, { Component } from 'react';

import Lobby from './Lobby';
import ClientIncomingCall from './ClientIncomingCall';
import CallStatus from './shared/CallStatus';
import Chat from './shared/Chat';
import CallScreen from './shared/CallScreen';
import Unsupported from './Unsupported';

import { defineMessages, FormattedMessage, intlShape } from 'react-intl';

import {Cam, Caret} from './shared/Icons'
import DetectRTC from 'detectrtc';

const messages = defineMessages({
  noWebcam: {
    id: "Shared.App.NoWebcam",
    defaultMessage: "Es wurde keine Webcam erkannt"
  },
  webcamBusy: {
    id: "Shared.App.WebcamBusy",
    defaultMessage: "Webcam ist belegt. Versuchen Sie es bitte erneut."
  },
  webcamDenied: {
    id: "Shared.App.WebcamDenied",
    defaultMessage: 'Sie müssen Zugriff auf die Webcam erlauben.'
  },
  webcamUnknownError: {
    id: "Shared.App.WebcamUnknownError",
    defaultMessage: 'Unbekannter Webcamfehler: {error}'
  }
});

const iceServers = [
  { urls: "stun:ms-comm.ca7cthywqan.nooon.io:443" },
  { urls: "turns:ms-comm.ca7cthywqan.nooon.io:443?transport=udp", 'username': 'nooon', 'credential': '7eQioJtCw9if' },
  { urls: "turns:ms-comm.ca7cthywqan.nooon.io:443?transport=tcp", 'username': 'nooon', 'credential': '7eQioJtCw9if' }
];

class ClientApp extends Component {
  static contextTypes = {
      intl: intlShape.isRequired,
  }
  
  constructor(props) {
    super(props);

    this.callStatusTimeout = null;
    this.socket = this.props.socket;
    this.state = this.getInitialState();

    if (this.state.featuresComplete) {
      const callSound = new Audio('incoming.mp3');
      callSound.loop = true;
      callSound.load();
      this.callSound = callSound;

      navigator.mediaDevices.ondevicechange = () => {
        console.log("Devices changed");
        this.readDevices();
      }
    }
    else {
      this.socket.emit('identify', {userAgent: window.navigator.userAgent});
      this.socket.emit('help', {
        user: this.props.user,
        appointment: this.props.appointment
      });
      this.socket.on('info', (helpinfo) => {
        this.setState({helpinfo: helpinfo});
      })
      return false;
    }

    this.socket.on('connect', () => {
      this.setState(this.getInitialState(), () => {
        this.socket.emit('identify', {userAgent: window.navigator.userAgent});
        this.socket.emit('enqueue', this.props.user);
        this.readDevices();
      });
    });

    this.socket.on('disconnect', () => {
      if (this.state.user) {
        if (this.state.user.pc) {
          this.state.user.pc.close();
          delete this.state.user.pc;
        }
        delete this.state.user.stream;

        this.state.user.online = false;
        this.setState({user: this.state.user});
      }

      if (this.state.client) {
        delete this.state.client.stream;
      }
    });

    this.socket.on('users', (users) => {
      const user = users.find(u => u.id == this.props.user);

      if (user && !user.online && this.state.user && this.state.user.pc) {
        this.state.user.pc.close();
        delete this.state.user.pc;
        delete this.state.user.stream;
        delete this.state.client.stream;

        this.clearCall();

        console.log("User went offline");
      }

      this.setState({
        user: user ? Object.assign({messages: []}, (this.state.user || {}), user) : null
      });
    });

    this.socket.on('pc', (msg) => this.processPcMsg(msg));
    this.socket.on('client', (client) => {
      console.log("client", client);
      this.setState({client: client})
    });
  }

  getInitialState() {
    let featuresComplete = this.isAbleToRun();
    return {
      user: null,
      client: null,
      currentScreen: featuresComplete ? 'lobby' : 'notsupported',
      appointment: this.props.appointment,
      videoDevices: [],
      audioDevices: [],
      muteRemote: false,
      muteLocal: false,
      disableCam: false,
      activeCamDevice: 0,
      activeVideoDevice: null,
      callStatus: null,
      windowWidth: 0,
      windowHeight: 0,
      featuresComplete: featuresComplete
    };
  }

  isAbleToRun() {
    return (
      navigator.mediaDevices &&
      DetectRTC.isWebRTCSupported &&
      DetectRTC.isWebSocketsSupported &&
      Audio &&
      !(document.documentMode || /Edge/.test(navigator.userAgent))
    );
  }

  slog(msg) {
    setTimeout(() => {
      this.socket.emit('syslog', msg);
    }, 16);

    var args = Array.prototype.slice.call(arguments);
    args.unshift('Syslog:');
    console.log.apply(console, args);
  }

  elog(msg) {
    var args = Array.prototype.slice.call(arguments);
    setTimeout(() => {
      this.socket.emit('errorlog', JSON.stringify(args));
    }, 16);

    args.unshift('Errorlog:');
    console.log.apply(console, args);
  }

  ulog(msg) {
    setTimeout(() => {
      this.socket.emit('actionlog', msg);
    }, 16);

    var args = Array.prototype.slice.call(arguments);
    args.unshift('Actionlog:');
    console.log.apply(console, args);
  }

  componentDidUpdate(pP, pS) {
    if (this.state.callStatus != pS.callStatus) {
      if (this.state.featuresComplete) {
        this.slog(`Callstatus: ${this.state.callStatus}`);
      }

      if (this.callStatusTimeout !== null) {
        clearTimeout(this.callStatusTimeout);
      }
      
      if (['stopped', 'denied'].indexOf(this.state.callStatus) !== -1) {
        this.state.callStatusTimeout = setTimeout(() => {
          this.clearCallStatus();
        }, 3000);
      }
    }
  }

  componentDidMount() {
    window.addEventListener('resize', () => {
      let width = window.innerWidth, height = window.innerHeight;

      if (width !== this.state.windowWidth || height !== this.state.windowHeight) {
        this.setState({
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight
        }, () => {
          if (this.state.featuresComplete) {
            this.slog(`Resolution: ${this.state.windowWidth}x${this.state.windowHeight}`);
          }
        });
      }
    });
  }

  readDevices() {
    let video = [];
    let audio = [];

    navigator.mediaDevices.enumerateDevices()
      .then((devices) => {
        console.log(devices);
        devices.forEach(d => {
          if (d.kind === 'videoinput') {
            video.push(d);
          }
          else if (d.kind === 'audioinput') {
            audio.push(d);
          }
        });
        
        this.setState({videoDevices: video, audioDevices: audio});

        this.slog(`Videodevices: ${video.length}, Audiodevices: ${audio.length}`);
      })
      .catch(e => { 
        this.elog(e);
      });
  }

  componentWillUpdate(nP, nS) {
    try {
      if (nS.incomingCall) {
        if (!this.state.incomingCall && this.callSound.paused) {
          this.callSound.currentTime = 0;
        }

        if (this.callSound.paused) {
          const playPromise = this.callSound.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.log("ERROR", error);
            });
          }
        }
      }
      else {
        this.callSound.pause();
      }
    } catch (e) {
      console.log(e);
    }
  }

  establishDataChannel(contact) {
    console.log("Establishing Datachannel RTC with", contact.id);

    if (contact.datapc && contact.datapc.iceConnectionState !== 'failed' && contact.datapc.iceConnectionState !== 'closed') {
      console.log("Datachannel exists, skipping", contact.pc);
      return;
    }

    const pc = new RTCPeerConnection({
      iceServers: iceServers
    });

    contact.datapc = pc;

    pc.onsignalingstatechange = (e) => {
      console.log("Datachannel Signaling state", pc.signalingState);

      if (pc.signalingState === 'closed') {
        delete contact.datapc;
      }
    };

    pc.onicecandidate = (evt) => {
      if (evt.candidate) {
        console.log("Datachannel Received local ice candidate", evt.candidate);
        this.socket.emit('pc', {
          'for': contact.id,
          'candidate': evt.candidate,
          'datachannel': true
        });
      }
    };

    pc.ondatachannel = (evt) => {
      contact.datachannel = evt.channel;
      console.log("Data channel incoming");

      contact.datachannel.onmessage = (evt) => {
        var data = JSON.parse(atob(evt.data));
        this.processDcMessage(data, contact);
      };

      contact.datachannel.onerror = function (error) {
        console.log("Data Channel Error:", error);
      };

      contact.datachannel.onopen = function () {
        console.log("Channel opened");
      };

      contact.datachannel.onclose = function () {
        console.log("Channel closed");
      };

      this.setState({
        user: contact
      });
    }
  }

  establishPeerconnection(contact) {
    console.log("Establishing RTC with", contact.id);

    if (contact.pc && contact.pc.iceConnectionState !== 'failed' && contact.pc.iceConnectionState !== 'closed') {
      console.log("PC", contact.pc);
      return;
    }

    const pc = new RTCPeerConnection({
      iceServers: iceServers
    });

    contact.pc = pc;

    contact.pc.onsignalingstatechange = (e) => {
      console.log("Signaling state", pc.signalingState);

      if (pc.signalingState === 'closed') {
        delete contact.pc;
      }
    };

    contact.pc.onnegotiationneeded = (evt) => {
      console.log("Negotiation needed", "skipping");
    };

    pc.onicecandidate = (evt) => {
      if (evt.candidate) {
        console.log("Received local ice candidate", evt.candidate);
        this.socket.emit('pc', {
          'for': contact.id,
          'candidate': evt.candidate
        });
      }
    };

    // once remote track arrives, show it in the remote video element
    pc.ontrack = (evt) => {
      console.log("Ontrack", evt);

      contact.stream = evt.streams[0];

      this.setState({
        user: contact
      });
    }
  }

  processDcMessage(data, contact) {
    if (data.type == 'chat') {
      const messages = contact.messages.slice();
      data.time = new Date(); // send time to arrival time
      messages.push(data);
      contact.messages = messages;
      this.setState({user: contact});
      this.slog(`Chat message received from ${contact.id}`);
    }
    else if (data.type === 'call') {
      const action = data.action;

      switch (action) {
        case 'stop':
          this.stopCall(data.hide, true);

          if (!data.hide) {
            this.setState({
              callStatus: 'stopped'
            });
          }
          break;
        case 'recover':
          this.switchCam();
          this.setState({
            callStatus: 'recovering'
          });
          break;
        case 'calling':
          this.setState({
            incomingCall: contact,
            callState: null
          });
          break;
        case 'callstart':
          this.showCall();
          break;
        case 'abort':
          this.setState({
            incomingCall: null
          });
          break;
        case 'accepted':
          this.setState({
            incomingCall: null,
            callStatus: 'accepted'
          });
        break;
        case 'denied':
          this.stopCall();
          this.setState({
            incomingCall: null,
            callStatus: 'denied'
          });
          break;
      }
    }
  }

  clearCallStatus() {
    this.setState({
      callStatus: null
    });
  }

  processPcMsg(message) {
    const contact = this.state.user;
    let pc = null;

    console.log("Incoming PC", message, "Data channel is", contact.datachannel ? contact.datachannel.readyState : 'not existing');

    if (message.desc || message.candidate) {
      if (message.datachannel) {
        if (!contact.datapc) {
          this.establishDataChannel(contact);
        }

        pc = contact.datapc;
      }
      else {
        if (!contact.pc) {
          this.establishPeerconnection(contact);
        }

        pc = contact.pc;
      }
    }

    setTimeout(() => {
      if (message.desc) {
        const desc = message.desc;

        if (!pc) {
          return;
        }

        // if we get an offer, we need to reply with an answer
        pc.setRemoteDescription(desc)
        .then(function () {
          console.log("Set remote description", desc);
          return pc.createAnswer();
        })
        .then((answer) => {
          pc.setLocalDescription(answer)
          .then(() => {
            console.log("Set local description", answer);
            this.socket.emit('pc', {
              'for': contact.id,
              'desc': answer,
              'datachannel': message.datachannel
            });
          })
          .catch((e) => console.log(e));
        })
        .catch((e) => console.log(e));
      }
      else if (message.candidate) {
        pc.addIceCandidate(message.candidate).catch((e) => console.log(e));
      }
      else {
        this.processDcMessage(message, contact);
      }
    }, 16);
  }

  showCall() {
    if (!this.state.user.pc) {
      this.establishPeerconnection(this.state.user);
    }

    if (!this.state.client.stream) {
      this.initialSetupLocalStream();
    }

    this.setState({currentScreen: 'call'});
  }

  initialSetupLocalStream(video) {
    if (this.state.settingUpLocalStream) {
      console.log("Skipping stream setup");
      return;
    }

    console.log("Initial Stream setup");
    this.setupLocalStream({});
  }

  setupLocalStream(video, _camDevice) {
    this.setState({
      settingUpLocalStream: true
    });

    console.log("Stream setup");

    let devices = this.state.videoDevices;
    let camDevice = _camDevice != null ? _camDevice : this.state.activeCamDevice;

    if (camDevice === null || camDevice >= devices.length) {
      camDevice = 0;
      video = {
        facingMode: 'user'
      };
      this.setState({activeCamDevice: 0})
    }

    video = !video ? {
      deviceId: {exact: devices[camDevice].deviceId}
    } : video;

    video = {
      facingMode: 'user'
    };

    navigator.mediaDevices.getUserMedia({
      audio: true,
      video: video
    })
    .then((stream) => {
      console.log("Got local stream");

      this.slog(`Access to webcam granted`);

      const currentStream = this.state.client.stream;

      this.state.client.stream = stream;

      if (this.state.user.pc) {
        stream.getTracks().forEach(track => this.state.user.pc.addTrack(track, stream));
        console.log("Added tracks to pc");
      }

      this.setState({
        client: this.state.client,
        settingUpLocalStream: false,
        activeCamDevice: camDevice
      }, () => {
        if (currentStream) {
          currentStream.getTracks().forEach((s) => {
            currentStream.removeTrack(s);
            s.stop();
          });

          if (this.state.user.pc) {
            console.log("Cleaning up stream");
            this.state.user.pc.removeStream(currentStream);
          }
        }

        this.sendMessage(this.state.user, {action: 'negotiate'}, 'call');
      });
    })
    .catch((e) => {
      console.log(e);

      this.elog(`Setup localstream ${e.name}`);

      if ("constraint" in e && e.constraint) {
        this.readDevices();
        var devices = this.state.videoDevices;

        this.setState({
          settingUpLocalStream: false
        }, () => {
          this.setupLocalStream(null, camDevice++);
        })
      }
      else {
        switch (e.name) {
          case 'NotFoundError':
          case 'DevicesNotFoundError':
            alert(this.context.intl.formatMessage(messages.noWebcam));
            break;
          case 'AbortError':
          case 'NotReadableError':
          case 'SourceUnavailableError':
            alert(this.context.intl.formatMessage(messages.webcamBusy));
            break;
          case 'PermissionDeniedError':
          case 'SecurityError':
          case 'NotAllowedError':
            alert(this.context.intl.formatMessage(messages.webcamDenied));
            break;
          default:
            alert(this.context.intl.formatMessage(messages.webcamUnknownError, {error: e.name}));
            break;
        }

        this.stopCall();
      }
    });
  }

  showChat() {
    this.setState({currentScreen: 'chat'});
  }

  startCall() {
    this.sendMessage(this.state.user, {action: 'init'}, 'call');
    this.setState({
      callStatus: 'calling',
      currentScreen: 'call'
    });
  }

  abortCall() {
    this.sendMessage(this.state.user, {action: 'abort'}, 'call');
    this.setState({
      callStatus: null,
      currentScreen: 'lobby'
    });
  }

  acceptCall() {
    this.stopCall();
    this.sendMessage(this.state.user, {action: 'accepted'}, 'call');
    this.setState({
      incomingCall: null
    });
  }

  denyCall(contact) {
    this.sendMessage(this.state.user, {action: 'denied'}, 'call');
    this.setState({
      incomingCall: null
    });
  }

  stopStreams() {
    if (this.state.client && this.state.client.stream) {
      this.state.client.stream.getTracks().forEach((s) => {
        this.state.client.stream.removeTrack(s);
        s.stop()
      });

      if (this.state.user.pc) {
        this.state.user.pc.removeStream(this.state.client.stream);
      }
    }

    if (this.state.user.stream) {
      this.state.user.stream.getTracks().forEach((s) => {
        this.state.user.stream.removeTrack(s);
        s.stop()
      });

      if (this.state.user.pc) {
        this.state.user.pc.removeStream(this.state.user.stream);
      }
    }

    delete this.state.client.stream;
    delete this.state.user.stream;
  }

  stopCall(hideStop, noSend) {
    this.stopStreams();

    if (!noSend) {
      this.sendMessage(this.state.user, {action: 'stop', hide: hideStop}, 'call');
    }

    this.clearCall();
    this.setState({
      client: this.state.client,
      incomingCall: null
    });
  }

  clearCall() {
    if (this.state.user.pc) {
      this.state.user.pc.close();
      delete this.state.user.pc;
    }

    this.setState({
      calling: null,
      muteRemote: false,
      muteLocal: false,
      user: this.state.user,
      disableCam: false,
      currentScreen: 'lobby',
      incomingCall: null,
      callStatus: null
    });
  }
  
  muteRemote() {
    let toggle = !this.state.muteRemote;

    if (this.state.user.stream) {
      this.state.user.stream.getAudioTracks().forEach(s => s.enabled = !toggle);
    }

    this.setState({muteRemote: toggle})

    this.ulog(`Remote ${toggle ? 'muted' : 'unmuted'}`);
  }
  
  muteLocal() {
    let toggle = !this.state.muteLocal;

    if (this.state.client.stream) {
      this.state.client.stream.getAudioTracks().forEach(s => s.enabled = !toggle);
    }

    this.setState({muteLocal: toggle})

    this.ulog(`Local ${toggle ? 'muted' : 'unmuted'}`);
  }
  
  disableCam() {
    let toggle = !this.state.disableCam;

    if (this.state.client.stream) {
      this.state.client.stream.getVideoTracks().forEach(s => s.enabled = !toggle);
    }

    this.setState({disableCam: toggle});

    this.ulog(`Cam ${toggle ? 'disabled' : 'enabled'}`);
  }
  
  switchCam() {
    let newCD = this.state.activeCamDevice + 1;

    if (newCD >= this.state.videoDevices.length) {
      newCD = 0;
    }

    this.setupLocalStream(null, newCD);

    this.ulog(`Cam switched`);
  }

  recoverStream(kind) {
    if (kind == 'local') {
      console.log("Not trying to recover local stream");
    }
    else if (kind == 'remote') {
      console.log("Trying to recover remote stream");
      if (this.state.user.pc) {
        this.sendMessage(this.state.user, {'action': 'recover'}, 'call');
        this.setState({
          callStatus: 'recovering'
        });
      }
    }
  }

  startChat() {
    this.showChat();
  }

  sendChatMessage(msg) {
    const user = this.state.user;
    let data = {
      from: this.state.client.id,
      text: msg,
      id: this.state.client.id + "-" + user.id + "-" + parseInt(Math.random() * 100000),
      time: new Date()
    };
    
    const messages = user.messages.slice();
    messages.push(data);
    user.messages = messages;
    
    this.sendMessage(user, data, 'chat');

    this.setState({user: user});

    this.ulog(`Chat message sent to ${this.state.user.id}`);
  }

  sendMessage(contact, data, type) {
    if (type === 'chat') {
      if (contact.datapc && contact.datachannel && contact.datachannel.readyState === 'open') {
        console.log("Sending chat message via datachannel");
        contact.datachannel.send(btoa(JSON.stringify(Object.assign({}, data, {type: type}))));
      }
      else {
        console.log("Datachannel not open, could not deliver chat message");
      }
    }
    else {
      // send via server
      data.for = contact.id;
      data.type = type;
      this.socket.emit('pc', data);
      console.log("Sent msg via server", data);
    }
  }

  streamsRunning() {
    this.setState({
      callStatus: 'established'
    });

    this.slog(`Streams running`);
  }

  render() {
    const isConnected = this.state.client && this.state.user && this.state.user.online;
    const screen = !this.state.featuresComplete || isConnected ? this.state.currentScreen : 'lobby';
    const hasUnreadMessages = this.state.user && this.state.user.messages ? !!this.state.user.messages.filter(m => !m.read).length : false;
    const showCallStatus = ['stopped', 'recovering', 'calling', 'accepted', 'denied'].indexOf(this.state.callStatus) != -1;

    return (
      <div style={{height: '100%'}} className={'flat ' + (this.state.incomingCall || showCallStatus ? 'hasOverlay' : '')}>
        {(screen === 'notsupported') &&
          <Unsupported
            info={this.state.helpinfo}
            user={this.props.user}
            locale={this.props.locale}
          />}
        {(screen === 'lobby') &&
          <Lobby
            locale={this.props.locale}
            user={this.state.user}
            appointment={this.state.appointment}
            hasUnreadMessages={hasUnreadMessages}
            onCall={() => this.startCall()}
            onChat={() => this.startChat()}
          />}
        {(screen === 'chat') &&
          <div style={{display:'flex', flexDirection: 'column', height: '100%'}}>
            <div className="screenheader">
              <div className="icon" onClick={() => this.setState({currentScreen: 'lobby'})}>
                <div>
                  <Caret />
                </div>
              </div>
              <div className="description" style={{padding:'0.2em'}}>
                <span style={{fontSize:'80%', display:'block'}}>
                  <FormattedMessage
                    id='Client.App.ChattingWith'
                    defaultMessage='Sie chatten mit {name}'
                    values={{
                      name: <span className="name" style={{fontWeight:600}}>{this.state.user.name}</span>
                    }} />
                </span>
              </div>
              <div className="icon" onClick={() => this.startCall()}>
                <div>
                  <Cam />
                </div>
              </div>
            </div>
            <div style={{flex: 1, overflow:'hidden'}}>
              <Chat
                remote={this.state.user}
                local={this.state.client}
                messages={this.state.user.messages}
                events={[]}
                onMessage={(msg) => this.sendChatMessage(msg)}
                onMessagesUpdate={(messages) => {
                  this.state.user.messages = messages;

                  this.setState({
                    user: this.state.user
                  });
                }}
              />
            </div>
          </div>}
          {(screen === 'call') &&
            <CallScreen
              style={{width: this.state.windowWidth + 'px', height: this.state.windowHeight + 'px'}}
              onError={(kind) => this.recoverStream(kind)}
              onStreamsRunning={() => this.streamsRunning()}
              onMuteRemote={() => this.muteRemote()}
              onMuteLocal={() => this.muteLocal()}
              onStopCall={() => this.stopCall()}
              onDisableCam={() => this.disableCam()}
              onSwitchCam={() => this.switchCam()}
              muteRemote={this.state.muteRemote}
              muteLocal={this.state.muteLocal}
              disableCam={this.state.disableCam}
              remote={this.state.user}
              local={this.state.client}
              callStatus={this.state.callStatus}
              audioDevices={this.state.audioDevices}
              videoDevices={this.state.videoDevices}
            />}
          {!this.state.incomingCall && showCallStatus &&
            <CallStatus
              callStatus={this.state.callStatus}
              onResetCallStatus={() => this.clearCallStatus()}
              onStopCall={() => this.stopCall()}
              onAbortCall={() => this.abortCall()}
            />
          }
          {(this.state.incomingCall &&
            <ClientIncomingCall
              messages={this.props.messages}
              caller={this.state.incomingCall}
              onAccept={() => this.acceptCall()}
              onDeny={() => this.denyCall()}
            />
          )}
      </div>
    );
  }
}

export default ClientApp;



// WEBPACK FOOTER //
// ./src/ClientApp.js